import { useNavigate, useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Activity, Layers, Heart, Wrench, Users, Sparkles, ShieldCheck, Radio } from "lucide-react";
import Operacao from "@/pages/Operacao";
import OperacaoAvancada from "@/pages/OperacaoAvancada";
import PipelineInspector from "@/pages/PipelineInspector";
import RealtimeFlow from "@/pages/RealtimeFlow";
import CortexTomExplorer from "@/pages/CortexTomExplorer";
import CortexDmnInbox from "@/pages/CortexDmnInbox";
import CortexVerifiedEntities from "@/pages/CortexVerifiedEntities";
import CortexAffect from "@/pages/CortexAffect";
import CortexInjectionsProvenance from "@/components/cortex/CortexInjectionsProvenance";
import CortexDmnFunnelCards from "@/components/cortex/CortexDmnFunnelCards";
import CortexCognitiveFunnelCards from "@/components/cortex/CortexCognitiveFunnelCards";
import { useOrgRole } from "@/hooks/useOrgRole";

type TabKey =
  | "decisoes"
  | "pipeline"
  | "fluxo"
  | "tom_affect"
  | "avancado"
  | "dmn"
  | "entities";

const VALID_BASE: TabKey[] = ["decisoes", "pipeline", "fluxo", "tom_affect", "avancado"];

export default function CortexOSHub() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { can } = useOrgRole();
  const canDmn = can("cortex.dmn.read");
  const canEntities = can("cortex.entities.read");
  const VALID: TabKey[] = [
    ...VALID_BASE,
    ...(canDmn ? (["dmn"] as TabKey[]) : []),
    ...(canEntities ? (["entities"] as TabKey[]) : []),
  ];
  // Backwards compat: redirect old tab keys
  const rawParam = params.get("tab") || "decisoes";
  const aliasMap: Record<string, TabKey> = {
    intents: "pipeline",
    tom: "tom_affect",
    affect: "tom_affect",
    avancada: "avancado",
    cockpit: "avancado",
    "tempo-real": "fluxo",
    realtime: "fluxo",
  };
  const raw = (aliasMap[rawParam] ?? rawParam) as TabKey;
  const tab: TabKey = VALID.includes(raw) ? raw : "decisoes";
  const setTab = (v: string) => navigate(`/cortex-os?tab=${v}`, { replace: true });

  return (
    <div className="container mx-auto px-4 py-4 max-w-7xl">
      <div className="mb-4">
        <h1 className="text-2xl font-bold font-headline tracking-tight">Cortex OS</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Núcleo de raciocínio: decisões, pipeline e estados sociais/emocionais do grupo.
        </p>
      </div>
      <div className="mb-4 space-y-4">
        <CortexCognitiveFunnelCards />
        <CortexDmnFunnelCards />
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        {/* Mobile: rolagem horizontal com snap. Desktop (md+): linha completa. */}
        <div className="-mx-4 px-4 md:mx-0 md:px-0 overflow-x-auto md:overflow-visible scrollbar-thin">
          <TabsList
            className="
              inline-flex md:flex w-max md:w-full md:max-w-4xl gap-1
              h-auto p-1 snap-x snap-mandatory
            "
          >
            <TabsTrigger value="decisoes" className="gap-2 shrink-0 snap-start whitespace-nowrap">
              <Activity className="w-4 h-4" />
              <span>Decisões</span>
            </TabsTrigger>
            <TabsTrigger value="pipeline" className="gap-2 shrink-0 snap-start whitespace-nowrap">
              <Layers className="w-4 h-4" />
              <span>Pipeline</span>
            </TabsTrigger>
            <TabsTrigger value="fluxo" className="gap-2 shrink-0 snap-start whitespace-nowrap">
              <Radio className="w-4 h-4" />
              <span>Fluxo Tempo Real</span>
            </TabsTrigger>
            <TabsTrigger value="tom_affect" className="gap-2 shrink-0 snap-start whitespace-nowrap">
              <Heart className="w-4 h-4" />
              <span>ToM &amp; Affect</span>
            </TabsTrigger>
            <TabsTrigger value="avancado" className="gap-2 shrink-0 snap-start whitespace-nowrap">
              <Wrench className="w-4 h-4" />
              <span>Avançado</span>
            </TabsTrigger>
            {canDmn && (
              <TabsTrigger value="dmn" className="gap-2 shrink-0 snap-start whitespace-nowrap">
                <Sparkles className="w-4 h-4" />
                <span>DMN</span>
              </TabsTrigger>
            )}
            {canEntities && (
              <TabsTrigger value="entities" className="gap-2 shrink-0 snap-start whitespace-nowrap">
                <ShieldCheck className="w-4 h-4" />
                <span>Entidades</span>
              </TabsTrigger>
            )}
          </TabsList>
        </div>
        <TabsContent value="decisoes" className="mt-2"><Operacao /></TabsContent>
        <TabsContent value="pipeline" className="mt-2"><PipelineInspector /></TabsContent>
        <TabsContent value="fluxo" className="mt-2"><RealtimeFlow /></TabsContent>
        <TabsContent value="tom_affect" className="mt-2">
          <div className="space-y-6">
            <section>
              <h2 className="text-lg font-semibold mb-2 flex items-center gap-2"><Activity className="w-5 h-5" />Injeções recentes (proveniência real)</h2>
              <CortexInjectionsProvenance />
            </section>
            <section>
              <h2 className="text-lg font-semibold mb-2 flex items-center gap-2"><Users className="w-5 h-5" />Theory of Mind</h2>
              <CortexTomExplorer />
            </section>
            <section>
              <h2 className="text-lg font-semibold mb-2 flex items-center gap-2"><Heart className="w-5 h-5" />Affect</h2>
              <CortexAffect />
            </section>
          </div>
        </TabsContent>
        <TabsContent value="avancado" className="mt-2"><OperacaoAvancada /></TabsContent>
        {canDmn && <TabsContent value="dmn" className="mt-2"><CortexDmnInbox /></TabsContent>}
        {canEntities && <TabsContent value="entities" className="mt-2"><CortexVerifiedEntities /></TabsContent>}
      </Tabs>
    </div>
  );
}
